import React from 'react'
import {
  Control,
  FieldValues,
  Path,
  useController,
  useFormContext,
} from 'react-hook-form'
import Radio from './Radio'
import Typography from '@ui/typography'
import { Pressable } from 'react-native'
import cn from '@ui/utils/cn'

export type BaseFormRadioProps<T extends FieldValues = FieldValues> = {
  name: Path<T>
  control: Control<T>
  value?: any
  label?: string
  disabled?: boolean
}

export type FormRadioWithContextProps<T extends FieldValues = FieldValues> =
  Omit<BaseFormRadioProps<T>, 'control'>

const BaseFormRadio = <T extends FieldValues = FieldValues>({
  name,
  control,
  value,
  label,
  disabled,
}: BaseFormRadioProps<T>) => {
  const { field } = useController({
    name,
    control,
  })

  return (
    <Pressable
      onPress={() => field.onChange(value ?? true)}
      className={cn(
        'flex min-h-10 flex-row items-center gap-3',
        disabled && 'opacity-50'
      )}
      disabled={disabled}
    >
      <Radio
        value={value !== undefined ? field.value === value : field.value}
      />
      {label != null && <Typography variant="SMB">{label}</Typography>}
    </Pressable>
  )
}

export const FormRadioWithContext = <T extends FieldValues = FieldValues>({
  ...props
}: FormRadioWithContextProps<T>) => {
  const { control } = useFormContext<T>()

  return <BaseFormRadio {...props} control={control} />
}

export type FormRadioProps<T extends FieldValues> = {
  control?: Control<T>
} & FormRadioWithContextProps<T>

const FormRadio = <T extends FieldValues>(props: FormRadioProps<T>) => {
  const { control } = props

  if (!control) {
    return <FormRadioWithContext {...props} />
  }

  return <BaseFormRadio {...props} control={control} />
}

export default FormRadio
